<script>
  import { format } from 'svelte-i18n';
  import { ButtonToolbar } from '@sveltestrap/sveltestrap';
  import { createQuery } from '@tanstack/svelte-query';

  import { AssetCard } from '@client/components/asset-card';
  import { BulkDeleteModal } from '@client/components/bulk-delete-modal';
  import { EmptyState } from '@client/components/empty-state';
  import { FilterBar } from '@client/components/filter-bar';
  import { SearchFilter } from '@client/components/filters/search';
  import { LayoutControls } from '@client/components/layout-controls';
  import { NoMatches } from '@client/components/no-matches';
  import { getNotificationsContext } from '@client/components/notifications';
  import { RenameModal } from '@client/components/rename-modal';
  import { SelectCell, SelectHeaderCell, SelectionControls } from '@client/components/selection-controls';
  import { Table } from '@client/components/table';

  import { AssetsService } from '@client/services/assets';

  import { appStore } from '@client/stores/app';
  import { videosSettingsStore } from '@client/stores/page-settings';

  import { getExtention } from '@client/utils/files';
  import { QueryParams } from '@client/utils/query-params';
  import { createSelection, selectedItems } from '@client/utils/selection';

  import { secondsToHms } from '@common/utils/numbers';

  import { ActionsCell, FileMetaCell, FilenameCell, FileTypeCell, FileUrlCell, PreviewCell } from './cell-formatters';

  export let refetch = false;
  export let onAddAsset = () => {};

  let renderCardLayout = $appStore.mobile || $videosSettingsStore.cards || false;
  let sortDirection = 1;
  let sortKey = 'title';
  let search = QueryParams.get('search') || '';
  let showFilters = true;
  let initialized = false;

  let original = [];
  let filtered = [];
  let showRenameModal = false;
  let renameVideo = null;

  let selecting = false;
  let showBulkDelete = false;

  const selection = createSelection();

  const { addNotification } = getNotificationsContext();

  const notify = (text, type = 'success', autohide = 2500, position = 'top-right') => {
    addNotification({ text, type, autohide, position });
  };

  const queryParams = {};
  const queryFn = async () => {
    const response = await AssetsService.getAllVideos(queryParams);

    original = filtered = response;

    refetch = false;

    onChangeSearch(search);

    initialized = true;

    return response;
  };

  const query = createQuery({
    queryKey: ['assets-videos', queryParams],
    queryFn
  });

  const onChangeLayout = (which) => {
    renderCardLayout = which;
    $videosSettingsStore.cards = which;
  };

  const onChangeSearch = (value) => {
    if (!value) {
      filtered = original;
    } else {
      filtered = original.filter((row) => {
        const search = value.toLowerCase();

        return Object.values(row).some((field) => {
          if (typeof field === 'string') {
            return field.toLowerCase().includes(search);
          }

          return false;
        });
      });
    }

    selection.prune(filtered);
  };

  const onRenameUpdate = async (video, newName) => {
    const response = await AssetsService.updateVideo({ ...video, rulename: newName });

    if (response?.status >= 400) {
      notify($format('label.FAILURE.VIDEO_RENAME', { values: { rulename: video.rulename } }), 'error');
      return;
    }

    notify($format('label.SUCCESS.RENAME', { values: { from: video.rulename, to: newName } }));

    showRenameModal = false;
    queryFn();
  };

  const onDeleteHandler = async (video) => {
    const confirmed = confirm(
      $format('label.DELETE_PROMPT', { values: { type: $format('label.VIDEO').toLowerCase() } })
    );

    if (confirmed) {
      $appStore.loading = true;

      const response = await AssetsService.deleteVideo(video);

      $appStore.loading = false;

      if (response?.status >= 400) {
        notify($format('label.FAILURE.RULENAME_DELETE', { values: { rulename: video.rulename } }), 'error');
        return;
      }

      notify($format('label.SUCCESS.DELETE', { values: { type: video.rulename } }));

      queryFn();
    }
  };

  const onBulkDeleteFinish = async (failed) => {
    const deleted = selectedAssets.length - failed.length;

    if (deleted) {
      notify($format('label.BULK_DELETE.NOTIFY_SUCCESS', { values: { count: deleted } }));
    }

    // Leave only the failures selected so the user can retry them directly.
    if (failed.length) {
      selection.selectAll(failed);
    } else {
      selecting = false;
      selection.clear();
    }

    await queryFn();
  };

  const onClickRow = ({ detail }) => {
    if (!selecting || detail.event.target.closest('input, button, a')) {
      return;
    }

    selection.toggle(detail.row);
  };

  let metricsCache = {};

  let columns = [
    {
      key: 'preview',
      title: $format('label.PREVIEW'),
      value: ({ filename }) => filename,
      renderComponent: PreviewCell,
      headerClass: 'preview-cell'
    },
    {
      key: 'title',
      title: $format('label.TITLE'),
      value: ({ title }) => title,
      renderComponent: FilenameCell,
      sortable: true,
      headerClass: 'filename-cell'
    },
    {
      key: 'url',
      title: $format('label.URL'),
      value: ({ url }) => url,
      renderComponent: FileUrlCell,
      headerClass: 'url-cell',
      sortable: true
    },
    {
      key: 'meta',
      title: $format('label.SESSIONS'),
      value: ({ filesize }) => filesize,
      headerClass: 'metrics-cell',
      renderComponent: {
        component: FileMetaCell,
        props: {
          metricsCache,
          onUpdate: (cache) => {
            metricsCache = { ...cache };
          }
        }
      }
    },
    {
      key: 'duration',
      title: $format('label.DURATION'),
      value: ({ duration }) => secondsToHms(duration),
      headerClass: 'duration-cell',
      sortable: true
    },
    {
      key: 'type',
      title: $format('label.TYPE'),
      value: ({ videotype }) => getExtention(videotype),
      renderComponent: FileTypeCell,
      headerClass: 'type-cell',
      sortable: true
    },
    {
      key: 'actions',
      title: '',
      value: '',
      renderComponent: {
        component: ActionsCell,
        props: {
          onDelete: () => {
            queryFn();
          },
          onRename: (row) => {
            renameVideo = row;
            showRenameModal = true;
          }
        }
      },
      headerClass: 'actions-cell'
    }
  ];

  $: $appStore.loading = $query.isLoading || $query.isFetching;
  $: hasVideos = initialized && original.length > 0;
  // Rebuilt when `filtered` changes so the header checkbox tracks the visible rows.
  $: selectColumn = {
    key: 'select',
    title: { component: SelectHeaderCell, props: { selection, rows: filtered } },
    value: () => '',
    renderComponent: { component: SelectCell, props: { selection } },
    headerClass: 'select-cell'
  };
  $: tableColumns = selecting ? [selectColumn, ...columns.filter((column) => column.key !== 'actions')] : columns;
  $: selectedAssets = selectedItems(original, $selection);
  $: if (refetch) {
    queryFn();
  }
</script>

<div class="videos">
  {#if hasVideos}
    <div class="toolbar">
      <FilterBar bind:show={showFilters}>
        <div class="toolbar-right">
          <SearchFilter bind:search onChange={onChangeSearch} />
          {#if filtered.length !== original.length}
            <small class="count">
              {$format('label.MATCHES_X_OF_X_TYPE', {
                values: {
                  filtered: Number(filtered.length).toLocaleString(),
                  total: Number(original.length).toLocaleString(),
                  type: $format('label.VIDEOS')
                }
              })}
            </small>
          {/if}

          <ButtonToolbar class="align-items-center gap-2">
            <SelectionControls
              bind:selecting
              {selection}
              rows={filtered}
              id="videos"
              onDelete={() => (showBulkDelete = true)}
            />

            <LayoutControls {renderCardLayout} onChange={onChangeLayout} />
          </ButtonToolbar>
        </div>
      </FilterBar>
    </div>
    <div class="items">
      {#if filtered.length}
        {#if $appStore.mobile || renderCardLayout}
          <div class="asset-cards">
            {#each filtered as video}
              <AssetCard
                bind:data={video}
                onDelete={() => onDeleteHandler(video)}
                onRename={() => {
                  renameVideo = video;
                  showRenameModal = true;
                }}
                onSelect={selection.toggle}
                selected={$selection.has(video.rulename)}
                {selecting}
                type="video"
              />
            {/each}
          </div>
        {:else}
          <Table
            id="videos-table"
            classNameThead="sticky-header"
            emptyLabel={$format('label.MESSAGE.NO_MATCHES_VIDEOS')}
            bind:rows={filtered}
            columns={tableColumns}
            rowClass={(row) => ($selection.has(row.rulename) ? 'bg-primary-subtle' : '')}
            on:clickRow={onClickRow}
            bind:sortBy={sortKey}
            bind:sortOrder={sortDirection}
          />
        {/if}
      {:else}
        <NoMatches message={$format('label.MESSAGE.NO_MATCHES_VIDEOS')} />
      {/if}
    </div>
  {:else if initialized}
    <EmptyState
      icon="film"
      heading={$format('label.EMPTY_STATE.VIDEO_HEADING')}
      description={$format('label.EMPTY_STATE.VIDEO_DESCRIPTION')}
      action={$format('label.EMPTY_STATE.VIDEO_ACTION')}
      on:click={onAddAsset}
    />
  {/if}
</div>

<RenameModal
  bind:show={showRenameModal}
  data={renameVideo}
  name={renameVideo?.rulename ?? ''}
  existingNames={original.map((v) => v.rulename)}
  duplicateError={$format('label.RENAME_MODAL.ERROR.DUPLICATE_VIDEO')}
  onUpdate={onRenameUpdate}
/>

<BulkDeleteModal
  bind:show={showBulkDelete}
  items={selectedAssets}
  title={$format('label.BULK_DELETE.CONFIRM_VIDEOS', { values: { count: selectedAssets.length } })}
  onDelete={(item) => AssetsService.deleteVideo(item)}
  onFinish={onBulkDeleteFinish}
/>

<style lang="scss" src="./videos.scss"></style>
